"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getAllEdgeDefinitionTypesForObject = exports.getAllEdgeDefinitionTypes = exports.getAllChildEdgeAttributeDefinitions = exports.getAllParentEdgeAttributeDefinitions = exports.getKnownEdgeAttributeDefinition = exports.getEdgeAttributeDefintion = exports.getEdgeTypeIdFromTraversal = exports.getKnownEdgeDefinitionType = exports.getTypeAllChildEdgeDefinitionTypes = exports.getAllChildEdgeTypeMatrixEntries = exports.getSpecificEdgeTypeId = exports.getAllParentEdgeTypeMatrixEntries = exports.getChildEdgeTypeId = exports.getParentEdgeTypeId = void 0;
const iotv_v3_types_1 = require("@iotv/iotv-v3-types");
const EdgeTypeMatrix_1 = require("./EdgeTypeMatrix");
const debug_1 = require("../../util/lambda/debug");
const debug = debug_1.DEBUG_OVERRIDE || (debug_1.NOT_LAMBDA && false);
/** 2024 03 Note: EdgeTypeMatrix Mutation
 * The edgeTypeMatrix is mutated by the index of @iotv/datamodel such that the mutated map is exported from @iotv/datamodel-core for all consumers
 * file:///./../../../../datamodel/src/index.ts
 *
 * ie non-core, external project edgeTypes are included. As observered in UI Listerlizer where an import from @iotv/datamodel-core of getParentEdgeTypeId correctly
 * resolves external project edges.
 *
 * Look here if unwanted side effects are seen in the future.
 * Resolution is to move edge validation function to this module.
 */
/** sorts either EdgeTypeDefinition or EdgeAttributeDefinition by cardinality  */
const sortEdgeAttributeDefinitionEntiresByCardinality = (entryA, entryB) => {
    const [ka, va] = entryA;
    const [kb, vb] = entryB;
    const cardinalityA = (0, iotv_v3_types_1.getCardinality)(va.cardinality?.in ?? null);
    const cardinalityB = (0, iotv_v3_types_1.getCardinality)(vb.cardinality?.in ?? null);
    return cardinalityA === iotv_v3_types_1.Cardinality.ONE ? -1 : cardinalityB === iotv_v3_types_1.Cardinality.ONE ? 1 : 0;
};
/** returns the first edgeType defined in the edgeTypeMatrix, where parentType is the source ( outV ) and childType is the target ( inV )  */
const getParentEdgeTypeId = (childType, parentType) => {
    const possibleEdgeDefinitions = Object.values((0, exports.getAllParentEdgeAttributeDefinitions)(childType));
    const filteredEdgeDefinitions = possibleEdgeDefinitions.filter((def) => def.objectTypeId === parentType);
    if (filteredEdgeDefinitions.length > 1) {
        console.warn(`WARN more that one possible edgeTypeId on ${parentType} to ${childType}, ${filteredEdgeDefinitions.map((def) => def.edgeTypeId).join(', ')}. returning first`);
    }
    return filteredEdgeDefinitions?.[0]?.edgeTypeId;
};
exports.getParentEdgeTypeId = getParentEdgeTypeId;
/**
 * returns the first edgeType defined in the edgeTypeMatrix, where parentType is the source ( outV ) and childType is the target ( inV )
 * This function performs the opposite lookup to getParentEdgeTypeId, and should return the same result when both functions use the same parent and child types
 */
const getChildEdgeTypeId = (parentType, childType) => {
    const possibleEdgeDefinitions = Object.values((0, exports.getAllChildEdgeAttributeDefinitions)(parentType));
    const filteredEdgeDefinitions = possibleEdgeDefinitions.filter((def) => def.objectTypeId === childType);
    if (filteredEdgeDefinitions.length > 1) {
        debug &&
            console.log(`WARN more that one possible edgeTypeId on ${parentType} to ${childType}, ${filteredEdgeDefinitions.map((def) => def.edgeTypeId).join(', ')}. returning first`);
    }
    return filteredEdgeDefinitions?.[0]?.edgeTypeId;
};
exports.getChildEdgeTypeId = getChildEdgeTypeId;
/** 2024 04 returns map of arrays of entries of edgeTypeMatrix where modelTypeIn is child on canonical edgeType, keyed by canonical source - eg in replacement for list generation */
const getAllParentEdgeTypeMatrixEntries = (modelTypeIn) => {
    const imposterMatrix = EdgeTypeMatrix_1.typedEdgeTypeMatrix;
    let edgeMatrixEntries = {};
    Object.entries(imposterMatrix).forEach(([modelTypeOut, possibleModelTypeInEntries]) => Object.entries(possibleModelTypeInEntries)
        .filter(([possibleModelTypeIn, _edgeTypeMap]) => modelTypeIn === possibleModelTypeIn)
        .forEach(([_possibleModelTypeIn, edgeTypeMap]) => Object.assign(edgeMatrixEntries, { [modelTypeOut]: Object.values(edgeTypeMap) })));
    return edgeMatrixEntries;
};
exports.getAllParentEdgeTypeMatrixEntries = getAllParentEdgeTypeMatrixEntries;
/** A helper method to explicitly catch errors when getting a specific edge type ID. */
const getSpecificEdgeTypeId = (childType, parentType, desiredId) => {
    const possibleEdgeDefinitions = Object.values((0, exports.getAllChildEdgeAttributeDefinitions)(parentType));
    const filteredEdgeDefinitions = possibleEdgeDefinitions.filter((def) => def.objectTypeId === childType);
    const desiredEdgeDefinition = filteredEdgeDefinitions.filter((d) => d.edgeTypeId === desiredId)[0];
    if (!desiredEdgeDefinition || !desiredEdgeDefinition.edgeTypeId) {
        throw new Error(`EdgeTypeId '${desiredId}' not found between ${parentType}->${childType}. Options are ${filteredEdgeDefinitions.map((f) => f.edgeTypeId).join(', ')}`);
    }
    return desiredEdgeDefinition.edgeTypeId;
};
exports.getSpecificEdgeTypeId = getSpecificEdgeTypeId;
/** 2024 04 returns map of all entries of edgeTypeMatrix where modelTypeOut is parent on canonical edgeType, keyed by canoical target - eg in replacement for list generation */
const getAllChildEdgeTypeMatrixEntries = (modelTypeOut) => {
    const imposterMatrix = EdgeTypeMatrix_1.typedEdgeTypeMatrix;
    let edgeMatrixEntries = {};
    const possibleModelTypeInEntries = imposterMatrix[modelTypeOut] ?? {};
    Object.entries(possibleModelTypeInEntries).forEach(([modelTypeIn, edgeTypeMap]) => Object.assign(edgeMatrixEntries, { [modelTypeIn]: Object.values(edgeTypeMap) }));
    return edgeMatrixEntries;
};
exports.getAllChildEdgeTypeMatrixEntries = getAllChildEdgeTypeMatrixEntries;
const getTypeAllChildEdgeDefinitionTypes = (modelTypeOut, modelTypeIn) => {
    const imposterMatrix = EdgeTypeMatrix_1.typedEdgeTypeMatrix;
    let edgeDefinitionTypes = {};
    if (imposterMatrix[modelTypeOut]) {
        const l1 = imposterMatrix[modelTypeOut];
        if (l1[modelTypeIn]) {
            edgeDefinitionTypes = l1[modelTypeIn];
        }
    }
    return edgeDefinitionTypes;
};
exports.getTypeAllChildEdgeDefinitionTypes = getTypeAllChildEdgeDefinitionTypes;
const getKnownEdgeDefinitionType = (modelTypeOut, modelTypeIn, edgeTypeId) => {
    const imposterMatrix = EdgeTypeMatrix_1.typedEdgeTypeMatrix;
    let edgeDefinitionType = undefined;
    const allTypeEdgeDefinitionTypes = (0, exports.getTypeAllChildEdgeDefinitionTypes)(modelTypeOut, modelTypeIn); //might be able to substitute
    if (imposterMatrix[modelTypeOut]) {
        const l1 = imposterMatrix[modelTypeOut];
        if (l1[modelTypeIn]) {
            const l2 = l1[modelTypeIn];
            if (l2.edgeTypeId) {
                edgeDefinitionType = l2[edgeTypeId];
            }
        }
    }
    return edgeDefinitionType;
};
exports.getKnownEdgeDefinitionType = getKnownEdgeDefinitionType;
/** Returns edgeTypeId give a from and to (non canonical direction ) objectTypeId and a Traversal sense */
const getEdgeTypeIdFromTraversal = ({ fromType, toType, traversal }) => {
    switch (traversal) {
        case iotv_v3_types_1.Traversal.ASCENDING:
            return (0, exports.getChildEdgeTypeId)(toType, fromType);
        case iotv_v3_types_1.Traversal.DESCENDING:
            return (0, exports.getChildEdgeTypeId)(fromType, toType);
    }
};
exports.getEdgeTypeIdFromTraversal = getEdgeTypeIdFromTraversal;
/** EDGE ATTRIBUTE DEFINITION FNS - THESE ARE USED AS A SHIM TO WORK WITH THE LEGACY METHOD OF GENERATING LISTS FROM object.getUserAttribues in UI - UNDER DEPRECATION */
/** given an edgeDefinitionType entry from the edgeTypeMatix, plus the modelTypeOut ( canonical source ) and modelTypeIn ( canonical target ) returns a object of type EdgeAttributeDefinition which is compatible with the form used in UI ( AttributeDefinition ) where it is used to automatically generate ViewDefinitions
 * In this object, objectTypeId and sourceObjectType have the following meaning:
 *  - objectTypeId: the type of related object relative to the page context object
 *  - sourceObjectType: the type of the page context object
 *
 *  - modelTypeOut is canonical source ( outV )
 *  - modelTypeIn is the canonical target ( inV )
 */
const getEdgeAttributeDefintion = (modelTypeOut, modelTypeIn, edgeDefinitionType, direction) => {
    const attDef = {
        type: 'RelatedType',
        edgeTypeId: edgeDefinitionType.edgeTypeId,
        cardinality: edgeDefinitionType.cardinality,
        editable: true,
        direction,
    };
    if (direction === iotv_v3_types_1.PathDirection.child) {
        attDef.label = edgeDefinitionType.labels.out;
        ((attDef.objectTypeId = modelTypeIn), (attDef.sourceObjectType = modelTypeOut));
    }
    else if (direction === iotv_v3_types_1.PathDirection.parent) {
        attDef.label = edgeDefinitionType.labels.in;
        ((attDef.objectTypeId = modelTypeOut), (attDef.sourceObjectType = modelTypeIn));
    }
    else {
        attDef.label = edgeDefinitionType.labels.out;
        ((attDef.objectTypeId = modelTypeIn), (attDef.sourceObjectType = modelTypeOut));
    }
    return attDef;
};
exports.getEdgeAttributeDefintion = getEdgeAttributeDefintion;
const getKnownEdgeAttributeDefinition = (modelTypeOut, modelTypeIn, edgeTypeId, direction) => {
    let edgeDefinitionType = (0, exports.getKnownEdgeDefinitionType)(modelTypeOut, modelTypeIn, edgeTypeId);
    return edgeDefinitionType ? (0, exports.getEdgeAttributeDefintion)(modelTypeOut, modelTypeIn, edgeDefinitionType, direction) : undefined;
};
exports.getKnownEdgeAttributeDefinition = getKnownEdgeAttributeDefinition;
/** For a given objectTypeId (modelTypeOut) returns all possible Parent->Child ( outV -> inV ) edgeAttributeDefinitions, ie where the objectTypeId is defined as the target ( inV, Child )
 * use this if its needed to find the correct edgeTypeId between modelType in and a Parent / outV / source objectType ie the reverse of the canonical definition in the edgeTypeMatrix
 */
const getAllParentEdgeAttributeDefinitions = (modelTypeIn) => {
    const imposterMatrix = EdgeTypeMatrix_1.typedEdgeTypeMatrix;
    let edgeAttribtueDefinition = {};
    Object.entries(imposterMatrix).forEach(([modelTypeOut, possibleModelTypeInEntries]) => Object.entries(possibleModelTypeInEntries)
        .filter(([possibleModelTypeIn, _edgeTypeMap]) => modelTypeIn === possibleModelTypeIn)
        .forEach(([_possibleModelTypeIn, edgeTypeMap]) => Object.entries(edgeTypeMap).forEach(([edgeKey, edgeTypeDefinition]) => Object.assign(edgeAttribtueDefinition, {
        [`${modelTypeIn}_in_${edgeKey}_${modelTypeOut}`]: (0, exports.getEdgeAttributeDefintion)(modelTypeOut, modelTypeIn, edgeTypeDefinition, iotv_v3_types_1.PathDirection.parent),
    }))));
    return Object.fromEntries(Object.entries(edgeAttribtueDefinition).sort(sortEdgeAttributeDefinitionEntiresByCardinality));
};
exports.getAllParentEdgeAttributeDefinitions = getAllParentEdgeAttributeDefinitions;
/** For a given objectTypeId (modelTypeOut) returns all possible Parent->Child ( outV -> inV ) edgeAttributeDefinitions, ie where the objectTypeId is defined as the source ( outV, Parent ) */
const getAllChildEdgeAttributeDefinitions = (modelTypeOut) => {
    const imposterMatrix = EdgeTypeMatrix_1.typedEdgeTypeMatrix;
    let edgeAttribtueDefinition = {};
    const possibleModelTypeInEntries = imposterMatrix[modelTypeOut] ?? {};
    Object.entries(possibleModelTypeInEntries).forEach(([modelTypeIn, edgeTypeMap]) => Object.entries(edgeTypeMap).forEach(([edgeKey, edgeTypeDefinition]) => Object.assign(edgeAttribtueDefinition, {
        [`${modelTypeOut}_out_${edgeKey}_${modelTypeIn}`]: (0, exports.getEdgeAttributeDefintion)(modelTypeOut, modelTypeIn, edgeTypeDefinition, iotv_v3_types_1.PathDirection.child),
    })));
    return Object.fromEntries(Object.entries(edgeAttribtueDefinition).sort(sortEdgeAttributeDefinitionEntiresByCardinality));
};
exports.getAllChildEdgeAttributeDefinitions = getAllChildEdgeAttributeDefinitions;
/** For a given objectTypeId (modelTypeOut) returns all possible edgeAttributeDefinitions where the objectTypeId is either the source ( outV, Parent ) or target (inV, Child ) */
const getAllEdgeDefinitionTypes = (modelType) => Object.assign((0, exports.getAllParentEdgeAttributeDefinitions)(modelType), (0, exports.getAllChildEdgeAttributeDefinitions)(modelType));
exports.getAllEdgeDefinitionTypes = getAllEdgeDefinitionTypes;
/** For a given object (possibleBusinessObject) returns all possible edgeAttributeDefinitions where the objectTypeId is either the source ( outV, Parent ) or target (inV, Child ), takes into account the special case of groups where the supportedType determines which edgeType is used */
const getAllEdgeDefinitionTypesForObject = (possibleBusinessObject) => {
    let edgeDefinitions = (0, exports.getAllEdgeDefinitionTypes)(possibleBusinessObject.type);
    let deviceEnabledTypeEdgeDefinitions = undefined;
    if (possibleBusinessObject.type === 'Group') {
        console.log('Group edgeDefinitions', edgeDefinitions);
        /** 2024 04 moved from UI, removes group subtypes where type is not same as possibleBusinessObject.supportedType  */
        edgeDefinitions = Object.fromEntries(Object.entries(edgeDefinitions).filter(([k, v]) => {
            return (v.edgeTypeId?.toLocaleLowerCase().includes(possibleBusinessObject.supportedType?.toLowerCase() ?? 'never') ||
                v.edgeTypeId?.includes(EdgeTypeMatrix_1.typedEdgeTypeMatrix.Group?.Group?.group_has_subGroup.edgeTypeId ?? 'never'));
        }));
        /* REMOVED 2024 04 replaced by above
            deviceEnabledTypeEdgeDefinitions = Object.fromEntries(Object.entries(edgeDefinitions).filter(([ k, v ]) => {
                ** this predicate that the edgeTypeId of a group to entity type edge must have the supportedType in the edge name is
                 * correct but very fragile
                 *
                return v.edgeTypeId?.toLocaleLowerCase().includes((possibleBusinessObject as Group).supportedType?.toLowerCase() ?? 'never') || v.edgeTypeId === (edgeTypeMatrix.Group?.Group?.group_has_subGroup?.edgeTypeId ?? 'never')
            }))
            */
    }
    return { /*...deviceEnabledTypeEdgeDefinitions,*/ ...edgeDefinitions };
};
exports.getAllEdgeDefinitionTypesForObject = getAllEdgeDefinitionTypesForObject;
